import React, {useCallback, useEffect, useState} from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {Button, DialogActions, DialogContent} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import Call from "../../hocs/call";
import ConfirmationDialog from "../confirmation-dialog";
import CustomJsonForms from "../custom-json-forms";
import FullscreenDialog from "../fullscreen-dialog";
import MaterialTable from "../material-table";
import SettingsDialog from "../settings-dialog";
import {appSelector} from "../../state/app/appSelectors";
import {nodeIdSelector} from "../../state/node/nodeSelectors";
import {
  createPackageLicense,
  deletePackageLicense,
  fetchPackageLicenseList,
  fetchPackageLicenseSchema,
  getPackageLicense,
  hidePackageLicense,
  hidePackageLicenseList,
  showPackageLicense,
  updatePackageLicense
} from "../../state/package-manager/packageManagerActions";
import {packageLicensesManagerSelector} from "../../state/package-manager/packageManagerSelectors";
import {localizeI18nObj} from "../../utils/i18n";

const PackageLicensesManager = () => {
  const {t} = useTranslation();
  const dispatch = useDispatch();

  const {language, languages} = useSelector(appSelector);

  const nodeId = useSelector(nodeIdSelector);
  const {isVisible, packageLicenses, schema, uiSchema, packageLicenseId, packageLicense} =
    useSelector(packageLicensesManagerSelector);

  const [packageLicenseToDelete, setPackageLicenseToDelete] = useState(null);

  const [data, setData] = useState(null);
  const [errors, setErrors] = useState(null);
  const [showValidation, setShowValidation] = useState(false);

  const handleFetchPackageLicenses = useCallback(() => {
    dispatch(fetchPackageLicenseList(nodeId));
  }, [dispatch, nodeId]);

  const handleClosePackageLicensesDialog = useCallback(() => {
    dispatch(hidePackageLicenseList());
  }, [dispatch]);

  const handleClosePackageLicenseDialog = useCallback(() => {
    dispatch(hidePackageLicense());
  }, [dispatch]);

  const handleCreatePackageLicense = useCallback(() => {
    dispatch(showPackageLicense());
  }, [dispatch]);

  const handleEditPackageLicense = useCallback(
    (_, {__Guid, title}) => {
      dispatch(getPackageLicense(nodeId, __Guid, title));
    },
    [dispatch, nodeId]
  );

  const handleFetchPackageLicenseSchema = useCallback(() => {
    dispatch(fetchPackageLicenseSchema(nodeId));
  }, [dispatch, nodeId]);

  const handleSubmitPackageLicense = useCallback(() => {
    if ((errors || []).length > 0) {
      setShowValidation(true);
    } else {
      setShowValidation(false);
      if (packageLicenseId) {
        dispatch(updatePackageLicense(nodeId, packageLicenseId, data));
      } else {
        dispatch(createPackageLicense(nodeId, data));
      }
    }
  }, [dispatch, errors, packageLicenseId, nodeId, data]);

  const handleStartDelete = useCallback((_, packageLicense) => {
    setPackageLicenseToDelete(packageLicense);
  }, []);

  const handleAbortDelete = useCallback(() => {
    setPackageLicenseToDelete(null);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    dispatch(deletePackageLicense(nodeId, packageLicenseToDelete.__Guid, packageLicenseToDelete.title));
    setPackageLicenseToDelete(null);
  }, [dispatch, nodeId, packageLicenseToDelete]);

  useEffect(() => {
    setData(packageLicense);
    setErrors(null);
    setShowValidation(false);

    return () => {
      setData(null);
      setErrors(null);
      setShowValidation(false);
    };
  }, [packageLicense]);

  if (!nodeId || !isVisible) {
    return null;
  }

  return (
    <SettingsDialog
      title={t("modules.packageManager.packageLicensesManager.dialog.title")}
      open={isVisible}
      onClose={handleClosePackageLicensesDialog}
      className="package-licenses-manager__package-licenses-dialog"
    >
      <Call cb={handleFetchPackageLicenses} disabled={packageLicenses !== null}>
        <MaterialTable
          id={"package-licenses-manager__table"}
          data={packageLicenses || []}
          rightActions={[
            {
              id: "package-licenses-manager__table__actions__create",
              label: t("modules.packageManager.packageLicensesManager.actions.create.label"),
              startIcon: <AddIcon />,
              onClick: handleCreatePackageLicense
            }
          ]}
          actions={[
            {
              icon: <EditIcon />,
              tooltip: t("modules.packageManager.packageLicensesManager.actions.edit.tooltip"),
              onClick: handleEditPackageLicense,
              className: "package-licenses-manager__table__actions__edit"
            },
            {
              icon: <DeleteIcon />,
              tooltip: t("modules.packageManager.packageLicensesManager.actions.delete.tooltip"),
              onClick: handleStartDelete,
              className: "package-licenses-manager__table__actions__delete"
            }
          ]}
          columns={[
            {
              field: "__Guid",
              title: t("modules.packageManager.packageLicensesManager.columns.id.label"),
              hidden: true,
              getClassName: () => "package-licenses-manager__table__columns__id"
            },
            {
              field: "title",
              title: t("modules.packageManager.packageLicensesManager.columns.title.label"),
              render: ({title}) => localizeI18nObj(title, language, languages),
              getClassName: () => "package-licenses-manager__table__columns__id"
            },
            {
              field: "parent",
              title: t("modules.packageManager.packageLicensesManager.columns.parent.label"),
              render: ({parent}) => {
                const packageLicenseParent = packageLicenses?.find(pl => pl.__Guid === parent);
                return localizeI18nObj(packageLicenseParent?.title, language, languages);
              },
              getClassName: () => "package-licenses-manager__table__columns__parent"
            }
          ]}
        />

        <ConfirmationDialog
          title={t("modules.packageManager.packageLicensesManager.deleteDialog.title")}
          content={t("modules.packageManager.packageLicensesManager.deleteDialog.description")}
          open={packageLicenseToDelete !== null}
          onClose={handleAbortDelete}
          onConfirm={handleConfirmDelete}
          className="package-licenses-manager__delete-package-license-dialog"
        />

        <FullscreenDialog
          open={packageLicense !== null}
          title={
            packageLicenseId
              ? t("modules.packageManager.packageLicensesManager.editDialog.title")
              : t("modules.packageManager.packageLicensesManager.createDialog.title")
          }
          onClose={handleClosePackageLicenseDialog}
          className="package-licenses-manager__package-license-dialog"
        >
          <DialogContent>
            <Call cb={handleFetchPackageLicenseSchema} disabled={schema !== null}>
              {schema && uiSchema && (
                <CustomJsonForms
                  schema={schema}
                  uischema={uiSchema}
                  data={data}
                  setData={setData}
                  errors={errors}
                  setErrors={setErrors}
                  showValidation={showValidation}
                  i18nPrefix={"modules.packageManager.packageLicensesManager"}
                />
              )}
            </Call>
          </DialogContent>
          <DialogActions>
            <Button
              className="package-licenses-manager__package-license-dialog__cancel-btn"
              onClick={handleClosePackageLicenseDialog}
            >
              {t("commons.confirm.cancel")}
            </Button>
            <Button
              className="package-licenses-manager__package-license-dialog__submit-btn"
              onClick={handleSubmitPackageLicense}
            >
              {t("commons.confirm.submit")}
            </Button>
          </DialogActions>
        </FullscreenDialog>
      </Call>
    </SettingsDialog>
  );
};

export default PackageLicensesManager;
